import {Injectable} from '@angular/core';
import {ApiService} from '@core/services/api.service';
import {IResponseApi} from '@core/models';

export type BusinessCallingRingTarget = 'PARTNERS' | 'META';

export interface IBusinessCallingEligibility {
  eligible: boolean;
  ringTarget?: BusinessCallingRingTarget | string;
}

export interface IBusinessCallingPermission {
  status?: 'HAS_PERMISSION' | string;
}

export type BusinessCallingChannel = 1 | 8;
const WHATSAPP_CHANNEL = 8;

export interface IBusinessCallingActionParams {
  pageId: string;
  callId: string;
  action: 'pre_accept' | 'accept' | 'reject' | 'terminate';
  session?: string;
  channel?: BusinessCallingChannel | number | string;
  phoneNumberId?: string;
}

@Injectable({
  providedIn: 'any'
})
export class BusinessCallingApiService extends ApiService {
  private getCallingEndpoint(channel: BusinessCallingChannel | number | string | undefined): string {
    return Number(channel) === WHATSAPP_CHANNEL
      ? '/chat/whatsapp/businesscalling'
      : '/chat/facebook/businesscalling';
  }

  checkPageEligible(params: { pageId: string }) {
    return this.postRequest<IResponseApi<IBusinessCallingEligibility>>(
      '/chat/facebook/businesscalling?act=checkPageEligible',
      {...params},
    );
  }

  setRingTarget(params: { pageId: string; ringTarget: BusinessCallingRingTarget }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=setRingTarget',
      {...params},
    );
  }

  /** Kiểm tra khách đã đồng ý cho Page gọi theo Meta Business Calling hay chưa. */
  checkCallPermission(params: { pageId: string; recipientId: string }) {
    return this.postRequest<IResponseApi<IBusinessCallingPermission>>(
      '/chat/facebook/businesscalling?act=checkCallPermission',
      {...params},
    );
  }

  /** Gửi message template xin quyền gọi tới khách hàng. */
  requestCallPermission(params: { pageId: string; recipientId: string }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=requestPermission',
      {...params},
    );
  }

  endCalling(params: Omit<IBusinessCallingActionParams, 'session'> & { action: 'reject' | 'terminate' }) {
    return this.postRequest<IResponseApi>(
      `${this.getCallingEndpoint(params.channel)}?act=endCalling`,
      {...params},
    );
  }

  acceptCalling(params: IBusinessCallingActionParams & { action: 'accept'; session: string }) {
    return this.postRequest<IResponseApi>(
      `${this.getCallingEndpoint(params.channel)}?act=acceptCalling`,
      {...params},
    );
  }

  preAcceptCalling(params: IBusinessCallingActionParams & { action: 'pre_accept'; session: string }) {
    return this.postRequest<IResponseApi>(
      `${this.getCallingEndpoint(params.channel)}?act=preAcceptCalling`,
      {...params},
    );
  }

  callToCustomer(params: { pageId: string; recipientId: string; session: string }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=callToCustomer',
      {...params},
    );
  }

  updateHandledBy(params: { pageId: string; callId: string; handledById: string }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=updateHandledBy',
      {...params},
    );
  }

  requestRecall(params: { pageId: string; recipientId: string }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=requestRecall',
      {...params},
    );
  }

  getIncomingList(params: { pageId: string|string[] }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=getIncomingList',
      {...params},
    );
  }

  getCallingList(params: { pageId: string; pageUserId?: string | null; after?: unknown }) {
    return this.postRequest<IResponseApi>(
      '/chat/facebook/businesscalling?act=listCalling',
      {...params},
    );
  }
}
